import { FC, useEffect, useState } from 'react'

// eslint-disable-next-line import/no-extraneous-dependencies
import { Button } from 'baby-design'
// eslint-disable-next-line import/no-extraneous-dependencies
import { Check, Copy, Location, Message } from 'baby-design/icons'

import { Address } from 'src/types/shipments'
import { multilineAddress } from 'src/routes/(registry)/registry/(gift-tracker-revamp)/utils'

import css from './ContactInfo.styles.scss'

const Name: FC<{ name: string }> = ({ name }) => (
  <div
    className={css.ContactInfo__Name}
    data-testid="GiftTracker__ContactInfo__Name"
  >
    {name}
  </div>
)

const EmailAddress: FC<{ email: string }> = ({ email }) => (
  <a className={css.ContactInfo__EmailAddress} href={`mailto:${email}`}>
    <span className={css.ContactInfo__EmailAddress__icon}>
      <Message />
    </span>
    <div className={css.ContactInfo__EmailAddress__text}>{email}</div>
  </a>
)

const useCopyToClipboard = (text: string): [boolean, () => void] => {
  const [isCopied, setIsCopied] = useState<boolean>(false)

  useEffect(() => {
    const timerMillis = 5000

    const id = setTimeout(() => {
      setIsCopied(false)
    }, timerMillis)

    if (isCopied) {
      navigator.clipboard.writeText(text)
    }

    return () => {
      clearTimeout(id)
    }
  }, [isCopied, setIsCopied, text])

  return [
    isCopied,
    () => {
      setIsCopied(true)
    },
  ]
}

const CopyButton: FC<{ address: string }> = ({ address }) => {
  const [isCopied, copyToClipboard] = useCopyToClipboard(address)

  const text = isCopied ? 'Copied!' : 'Copy'
  const icon = isCopied ? (
    <Check />
  ) : (
    <Copy className={css.ContactInfo__CopyButton__icon} />
  )

  return (
    <div className={css.ContactInfo__CopyButton}>
      <Button
        icon={{
          side: 'left',
          icon,
          size: 'sm',
        }}
        size="sm"
        variant="tertiary"
        onClick={() => copyToClipboard()}
      >
        {text}
      </Button>
    </div>
  )
}

const MailingAddress: FC<{
  thankYouNoteAddress: Address
}> = ({ thankYouNoteAddress }) => {
  const { string: address, node: rows } = multilineAddress({
    address: thankYouNoteAddress,
    includeCountry: false,
  })

  return (
    <div className={css.ContactInfo__MailingAddress}>
      <div className={css.ContactInfo__MailingAddress__Address}>
        <span className={css.ContactInfo__MailingAddress__Address__icon}>
          <Location />
        </span>
        <div className={css.ContactInfo__MailingAddress__Address__text}>
          {rows}
        </div>
      </div>
      <CopyButton {...{ address }} />
    </div>
  )
}

type ContactInfoProps = {
  email: string
  name?: string
  thankYouNoteAddress?: Address | null
}
const ContactInfo: FC<ContactInfoProps> = ({
  email,
  name,
  thankYouNoteAddress,
}) => (
  <div className={css.ContactInfo}>
    {name && <Name name={name} />}
    <div className={css.ContactInfo__Addresses}>
      {email && <EmailAddress email={email} />}
      {thankYouNoteAddress && (
        <MailingAddress thankYouNoteAddress={thankYouNoteAddress} />
      )}
    </div>
  </div>
)

export default ContactInfo
